import React from 'react';
import { AlertTriangle, Calendar, Mail, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const SuspendedAccountView: React.FC = () => {
  const { user, logout } = useAuth();
  const { select, locale } = useLanguage();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const isSuspensionActive = () => {
    if (!user?.suspended_until) return false;
    return new Date(user.suspended_until) > new Date();
  };

  const getSuspensionMessage = () => {
    if (!user) return '';

    if (user.account_status === 'banned') {
      return select({
        es: 'Tu cuenta ha sido permanentemente suspendida de SoArtistic.',
        en: 'Your account has been permanently suspended from SoArtistic.'
      });
    }

    if (user.account_status === 'suspended' && isSuspensionActive()) {
      return select({
        es: `Tu cuenta está temporalmente suspendida hasta el ${formatDate(user.suspended_until!)}.`,
        en: `Your account is temporarily suspended until ${formatDate(user.suspended_until!)}.`
      });
    }

    if (user.account_status === 'warned') {
      return select({
        es: 'Tu cuenta ha recibido una advertencia. Ten cuidado con futuras violaciones.',
        en: 'Your account has received a warning. Please avoid further violations.'
      });
    }

    return select({ es: 'Tu cuenta está bajo revisión.', en: 'Your account is under review.' });
  };

  const canAppeal = user?.account_status !== 'banned';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-full max-w-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 p-8 text-center">
          <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="text-white" size={40} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{select({ es: 'Cuenta suspendida', en: 'Account suspended' })}</h1>
          <p className="text-red-100 text-lg">{select({ es: 'Acceso temporalmente restringido', en: 'Access temporarily restricted' })}</p>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* User Info */}
          <div className="flex items-center gap-4 mb-8 p-4 bg-gray-50 rounded-xl">
            <img
              src={user?.avatar_url || 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=64&h=64&fit=crop'}
              alt={user?.full_name}
              className="w-16 h-16 rounded-full object-cover"
            />
            <div>
              <h2 className="text-xl font-bold text-gray-900">{user?.full_name}</h2>
              <p className="text-gray-600">@{user?.username}</p>
              <div className="flex items-center gap-2 mt-2">
                <div className={`w-3 h-3 rounded-full ${
                  user?.account_status === 'banned' ? 'bg-red-500' :
                  user?.account_status === 'suspended' ? 'bg-orange-500' :
                  'bg-yellow-500'
                }`}></div>
                <span className={`text-sm font-medium ${
                  user?.account_status === 'banned' ? 'text-red-700' :
                  user?.account_status === 'suspended' ? 'text-orange-700' :
                  'text-yellow-700'
                }`}>
                  {user?.account_status === 'banned'
                    ? select({ es: 'Cuenta baneada', en: 'Banned account' })
                    : user?.account_status === 'suspended'
                      ? select({ es: 'Cuenta suspendida', en: 'Suspended account' })
                      : select({ es: 'Cuenta advertida', en: 'Warned account' })}
                </span>
              </div>
            </div>
          </div>

          {/* Suspension Details */}
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-red-900 mb-3">{select({ es: 'Estado de la cuenta', en: 'Account status' })}</h3>
              <p className="text-red-800 text-lg font-medium mb-4">
                {getSuspensionMessage()}
              </p>
              
              {user?.suspension_reason && (
                <div className="bg-white border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-900 mb-2">{select({ es: 'Motivo:', en: 'Reason:' })}</h4>
                  <p className="text-red-700">{user.suspension_reason}</p>
                </div>
              )}
            </div>

            {/* Suspension Timeline */}
            {user?.account_status === 'suspended' && user?.suspended_until && (
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="text-orange-600" size={20} />
                  <h3 className="text-lg font-semibold text-orange-900">{select({ es: 'Duración de la suspensión', en: 'Suspension duration' })}</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-orange-700">{select({ es: 'Inicio:', en: 'Start:' })}</span>
                    <span className="font-medium text-orange-900">
                      {user.subscription_started_at ? formatDate(user.subscription_started_at) : select({ es: 'No disponible', en: 'Not available' })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-orange-700">{select({ es: 'Fin:', en: 'End:' })}</span>
                    <span className="font-medium text-orange-900">
                      {formatDate(user.suspended_until)}
                    </span>
                  </div>
                  
                  {isSuspensionActive() && (
                    <div className="mt-4 p-3 bg-white border border-orange-200 rounded-lg">
                      <p className="text-orange-800 text-sm">
                        <strong>{select({ es: 'Tiempo restante:', en: 'Time remaining:' })}</strong>{' '}{(() => {
                          const now = new Date();
                          const end = new Date(user.suspended_until);
                          const diffDays = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                          return diffDays > 0
                            ? select({ es: `${diffDays} días`, en: `${diffDays} days` })
                            : select({ es: 'Menos de un día', en: 'Less than a day' });
                        })()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* What you can do */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">{select({ es: '¿Qué puedes hacer?', en: 'What can you do?' })}</h3>
              
              <div className="space-y-4">
                {canAppeal && (
                  <div className="flex items-start gap-3">
                    <Mail className="text-blue-600 mt-1" size={18} />
                    <div>
                      <h4 className="font-medium text-blue-900">{select({ es: 'Apelar la decisión', en: 'Appeal the decision' })}</h4>
                      <p className="text-blue-700 text-sm mb-2">
                        {select({ es: 'Si crees que esta suspensión es un error, puedes contactarnos.', en: 'If you believe this suspension is a mistake, please contact us.' })}
                      </p>
                      <a
                        href={`mailto:apelaciones@soartistic.com?subject=${encodeURIComponent(select({ es: 'Apelación de suspensión', en: 'Suspension appeal' }))} - @${user?.username}`}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        <Mail size={16} />
                        {select({ es: 'Enviar apelación', en: 'Send appeal' })}
                      </a>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-amber-600 mt-1" size={18} />
                  <div>
                    <h4 className="font-medium text-blue-900">{select({ es: 'Revisa las normas', en: 'Review the guidelines' })}</h4>
                    <p className="text-blue-700 text-sm mb-2">
                      {select({ es: 'Familiarízate con nuestras normas de comunidad para evitar futuras suspensiones.', en: 'Review our community guidelines to avoid future suspensions.' })}
                    </p>
                    <a
                      href={select({ es: 'https://soartistic.com/normas-comunidad', en: 'https://soartistic.com/community-guidelines' })}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium"
                    >
                      {select({ es: 'Ver normas de comunidad', en: 'View community guidelines' })}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Warning Message */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-yellow-600 mt-1" size={20} />
                <div>
                  <h4 className="font-medium text-yellow-900 mb-2">{select({ es: 'Importante', en: 'Important' })}</h4>
                  <p className="text-yellow-800 text-sm">
                    {select({
                      es: 'Durante la suspensión, no puedes acceder a las funciones de la plataforma. Crear cuentas adicionales para evadir esta suspensión resultará en una prohibición permanente.',
                      en: 'While suspended, you cannot access platform features. Creating additional accounts to bypass this suspension will result in a permanent ban.'
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-8 pt-6 border-t border-gray-200 flex justify-center">
            <button
              onClick={logout}
              className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              <LogOut size={18} />
              {select({ es: 'Cerrar sesión', en: 'Log out' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuspendedAccountView;
